import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTable } from "@tanstack/react-table-v9";
import type {
  ColumnPinningState,
  ReactTable,
  Row,
  RowData,
  TableOptions,
  TableState,
} from "@tanstack/react-table-v9";

import {
  ACTIONS_COLUMN_ID,
  EXPANDER_COLUMN_ID,
  SELECTION_COLUMN_ID,
} from "../columns";
import type { TableV2ColumnDef } from "../columns";
import type { SelectAllOptions } from "../components/SelectionCells";
import { tableV2Features } from "../features";
import type { TableV2Features } from "../features";
import { renderDefaultCell } from "../renderDefaultCell";
import {
  currentHistoryEntry,
  usePersistedTableState,
  useRestoreScroll,
  writePersistedTableState,
} from "./usePersistedTableState";
import { usePivotView } from "./usePivotView";
import type {
  TableV2PivotOptions,
  TableV2PivotView,
  TableV2View,
} from "./usePivotView";
import { useRowSelection } from "./useRowSelection";
import type { RowSelectionOptions } from "./useRowSelection";

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [5, 10, 15, 25, 50, 100];

const hiddenByDefault = <TData extends RowData>(
  columns: TableV2ColumnDef<TData>[],
): Record<string, boolean> =>
  Object.fromEntries(
    columns.flatMap((column) => {
      const def = column as TableV2ColumnDef<TData> & {
        accessorKey?: string;
        columns?: TableV2ColumnDef<TData>[];
      };
      if (def.columns) return Object.entries(hiddenByDefault(def.columns));
      const id = def.id ?? def.accessorKey;
      return id && def.meta?.defaultHidden ? [[id, false]] : [];
    }),
  );

export interface TableV2Extras<TData> {
  view: TableV2View;
  setView: (view: TableV2View) => void;
  pivot: TableV2PivotView<TData> | null;
}

export type TableV2Instance<TData extends RowData> = ReactTable<
  TableV2Features,
  TData
> & {
  v2?: TableV2Extras<TData>;
  resetColumns?: () => void;
  onSelectAllRows?: (selected: boolean) => void;
  allRowsSelected?: boolean;
  // State came back from this browser tab, e.g. after visiting a detail page
  restored?: boolean;
  /** Slug of the saved view this tab's layout was applied from */
  restoredView?: string;
  setAppliedView?: (slug: string | undefined) => void;
  /** Visibility after resetting columns: default-hidden columns off */
  defaultColumnVisibility?: Record<string, boolean>;
  /** Sets the page size without saving it as the user's preference, e.g. from a shared view */
  applyPageSize?: (pageSize: number) => void;
  /** Selected rows, including ones selected on pages that are no longer loaded */
  getSelectedRows?: () => TData[];
};

export type TableV2State = TableState<TableV2Features>;

export interface PageSizeStorage {
  pageSize?: number;
  onPageSizeChange: (pageSize: number) => void;
}

export interface UseTableV2Options<
  TData extends RowData,
> extends RowSelectionOptions<TData> {
  data: TData[];
  columns: TableV2ColumnDef<TData>[];
  /** Stable id per row; defaults to `row.id`, then the row index */
  getRowId?: (
    row: TData,
    index: number,
    parent?: Row<TableV2Features, TData>,
  ) => string;
  /** Server total. When set, `data` is treated as the current page */
  rowCount?: number;
  /** Sorting happens on the server. Defaults to true when `rowCount` is set */
  manualSorting?: boolean;
  /** The search box filters on the server. Defaults to true when `rowCount` is set */
  manualFiltering?: boolean;
  /** Show every row on one page */
  disablePagination?: boolean;
  initialState?: Partial<TableV2State>;
  enableRowSelection?:
    | boolean
    | ((row: Row<TableV2Features, TData>) => boolean);
  /** Picking a row replaces the selection, and the select-all checkbox is hidden */
  singleRowSelection?: boolean;
  /** The header checkbox was clicked, e.g. to act on every row matching the filters rather than only this page */
  onSelectAllRows?: (selected: boolean) => void;
  /** Controlled "every row matching the filters" selection, so the header checkbox stays checked on other pages and unchecking it clears the selection everywhere */
  allRowsSelected?: boolean;
  getRowCanExpand?: (row: Row<TableV2Features, TData>) => boolean;
  getSubRows?: (row: TData, index: number) => TData[] | undefined;
  /** Saves page, sort, columns, view and scroll position for the browser tab. Search and expanded rows stay in memory */
  stateKey?: string;
  /** Used with `stateKey` to restore the scroll position only after rows have loaded */
  isLoading?: boolean;
  /** Where the chosen page size is saved, e.g. `useUserMetaPageSize(id)` */
  pageSizeStorage?: PageSizeStorage;
  /** Adds a Pivot view of the same data, with a Table / Pivot switch in the toolbar */
  pivot?: TableV2PivotOptions<TData>;
  /** Escape hatch for controlled state or other TanStack options */
  tableOptions?: Partial<
    Omit<TableOptions<TableV2Features, TData>, "features" | "data" | "columns">
  >;
}

const EDGE_START_IDS = [SELECTION_COLUMN_ID, EXPANDER_COLUMN_ID];
const EDGE_IDS = [...EDGE_START_IDS, ACTIONS_COLUMN_ID];

/**
 * Keeps the utility columns on the table's edges under any pinning, saved, caller-provided or from the header menu:
 * actions always pinned last, and selection / expand pinned first whenever another column is pinned to the start
 */
export const normalizePinning = (
  pinning: ColumnPinningState | undefined,
  columnIds: string[],
): ColumnPinningState => {
  const start = (pinning?.start ?? []).filter((id) => !EDGE_IDS.includes(id));
  const end = (pinning?.end ?? []).filter((id) => !EDGE_IDS.includes(id));
  const edgeStart = EDGE_START_IDS.filter((id) => columnIds.includes(id));
  return {
    start: start.length ? [...edgeStart, ...start] : start,
    end: columnIds.includes(ACTIONS_COLUMN_ID)
      ? [...end, ACTIONS_COLUMN_ID]
      : end,
  };
};

const defaultGetRowId = <TData extends RowData>(
  row: TData,
  index: number,
  parent?: Row<TableV2Features, TData>,
) => {
  const id = (row as { id?: unknown } | null)?.id;
  if (typeof id === "string" || typeof id === "number") return String(id);
  return parent ? `${parent.id}.${index}` : String(index);
};

const TYPE_SORT_FNS = {
  number: "numeric",
  currency: "numeric",
  percent: "numeric",
  date: "date",
  datetime: "date",
} as const;

/** Columns without their own `sortFn` sort by their `meta.type`, since the API sends numbers and dates as strings */
const withTypeSorting = <TData extends RowData>(
  columns: TableV2ColumnDef<TData>[],
): TableV2ColumnDef<TData>[] =>
  columns.map((column) => {
    const group = column as { columns?: TableV2ColumnDef<TData>[] };
    if (group.columns) {
      return { ...column, columns: withTypeSorting(group.columns) };
    }
    const type = column.meta?.type;
    if (column.sortFn || !type || !(type in TYPE_SORT_FNS)) return column;
    return {
      ...column,
      sortFn: TYPE_SORT_FNS[type as keyof typeof TYPE_SORT_FNS],
    };
  });

/** Runs `effect` when `key` changes, skipping the first render */
const useOnChange = (key: string, effect: () => void) => {
  const previous = useRef(key);
  useEffect(() => {
    if (previous.current === key) return;
    previous.current = key;
    effect();
    // Only the key should trigger this
  }, [key]);
};

export const useTableV2 = <TData extends RowData>({
  data,
  columns,
  getRowId = defaultGetRowId,
  rowCount,
  manualSorting,
  manualFiltering,
  disablePagination = false,
  initialState,
  enableRowSelection = true,
  singleRowSelection = false,
  selectedRowIds,
  onSelectedRowIdsChange,
  onSelectedRowsChange,
  onSelectAllRows,
  allRowsSelected,
  selectedRows,
  getRowCanExpand,
  getSubRows,
  stateKey,
  isLoading = false,
  pageSizeStorage,
  pivot,
  tableOptions,
}: UseTableV2Options<TData>): TableV2Instance<TData> => {
  const persisted = usePersistedTableState(stateKey);
  const [path] = useState(() => window.location.pathname);
  const entry = currentHistoryEntry();
  const isServer = rowCount !== undefined;

  // A first guess from the column defs; the table's own column ids correct it below
  const [initialVisibility] = useState(() => {
    return {
      ...hiddenByDefault(columns),
      ...initialState?.columnVisibility,
    };
  });

  const resolvedInitialState = useMemo<Partial<TableV2State>>(
    () => {
      return {
        ...initialState,
        sorting: persisted?.sorting ?? initialState?.sorting ?? [],
        globalFilter: initialState?.globalFilter ?? "",
        expanded: initialState?.expanded ?? {},
        columnVisibility: {
          ...initialVisibility,
          ...persisted?.columnVisibility,
        },
        columnOrder: persisted?.columnOrder ?? initialState?.columnOrder ?? [],
        columnPinning: persisted?.columnPinning ?? initialState?.columnPinning,
        columnSizing:
          persisted?.columnSizing ?? initialState?.columnSizing ?? {},
        pagination: {
          pageIndex:
            persisted?.pagination?.pageIndex ??
            initialState?.pagination?.pageIndex ??
            0,
          pageSize:
            persisted?.pagination?.pageSize ??
            pageSizeStorage?.pageSize ??
            initialState?.pagination?.pageSize ??
            DEFAULT_PAGE_SIZE,
        },
      };
    },
    // Initial state is read once, like TanStack's own initialState
    [],
  );

  const defaultColumn = useMemo(() => {
    return {
      size: 150,
      minSize: 60,
      maxSize: 800,
      sortFn: "caseInsensitive" as const,
      sortUndefined: "last" as const,
      cell: renderDefaultCell,
    };
  }, []);

  const typedColumns = useMemo(() => withTypeSorting(columns), [columns]);

  const selectAllOptions: SelectAllOptions = {
    onSelectAllRows,
    allRowsSelected,
  };

  // Filled in once the table exists; read by effects after render
  const tableRef = useRef<ReactTable<TableV2Features, TData> | null>(null);
  const selection = useRowSelection({
    data,
    getRowId,
    getFlatRows: () => tableRef.current?.getCoreRowModel().flatRows ?? [],
    selectedRowIds,
    onSelectedRowIdsChange,
    onSelectedRowsChange,
    selectedRows,
  });

  const [pinningState, setPinningState] = useState(
    resolvedInitialState.columnPinning,
  );
  const columnIdsKey = columns.map((column) => column.id ?? "").join("|");
  const pinning = useMemo(
    () =>
      normalizePinning(
        pinningState,
        columns.flatMap((column) => (column.id ? [column.id] : [])),
      ),
    [pinningState, columnIdsKey],
  );

  const table = useTable<TableV2Features, TData>({
    features: tableV2Features,
    data,
    columns: typedColumns,
    defaultColumn,
    getRowId,
    getSubRows,
    getRowCanExpand,
    enableRowSelection,
    enableMultiRowSelection: !singleRowSelection,
    initialState: resolvedInitialState,
    rowCount,
    manualPagination: isServer || disablePagination,
    manualSorting: manualSorting ?? isServer,
    manualFiltering: manualFiltering ?? isServer,
    // Every column sorts ascending first and can't go back to unsorted
    sortDescFirst: false,
    enableSortingRemoval: false,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    columnResizeMode: "onChange",
    globalFilterFn: "includesString",
    ...selection,
    // Cell and header renderers get TanStack's own table, which only shares options with this one
    ...selectAllOptions,
    ...tableOptions,
    state: {
      ...selection.state,
      columnPinning: pinning,
      ...tableOptions?.state,
    },
    onColumnPinningChange: (updater) => {
      setPinningState((current) =>
        typeof updater === "function"
          ? updater(current ?? { start: [], end: [] })
          : updater,
      );
      tableOptions?.onColumnPinningChange?.(updater);
    },
  });

  tableRef.current = table;

  const filteredModel = pivot && !isServer ? table.getFilteredRowModel() : null;
  const sourceRows = useMemo(
    () => filteredModel?.rows.map((row) => row.original),
    [filteredModel],
  );

  const pivotView = usePivotView({
    pivot,
    columns,
    sourceRows,
    serverMode: isServer,
    stateKey,
    initialView: persisted?.view,
    defaultColumn,
    search:
      typeof table.state.globalFilter === "string"
        ? table.state.globalFilter
        : undefined,
  });

  const {
    pagination,
    sorting,
    globalFilter,
    columnVisibility,
    columnOrder,
    columnPinning,
    columnSizing,
    columnResizing,
  } = table.state;
  const isResizing = !!columnResizing.isResizingColumn;

  const unsavedPageSize = useRef<number | null>(null);
  useOnChange(String(pagination.pageSize), () => {
    table.setPageIndex(0);
    if (unsavedPageSize.current === pagination.pageSize) {
      unsavedPageSize.current = null;
      return;
    }
    pageSizeStorage?.onPageSizeChange(pagination.pageSize);
  });
  const applyPageSize = (pageSize: number) => {
    if (pageSize === pagination.pageSize) return;
    unsavedPageSize.current = pageSize;
    table.setPagination({ pageIndex: 0, pageSize });
  };

  const [appliedView, setAppliedView] = useState(persisted?.savedView);
  useOnChange(JSON.stringify([sorting, globalFilter]), () =>
    table.setPageIndex(0),
  );

  // Keep a restored or stale page index inside the data that actually loaded
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex >= pageCount) {
      table.setPageIndex(pageCount - 1);
    }
  }, [pageCount, pagination.pageIndex, table]);

  // Sizes are written once the drag ends, not on every mouse move
  useEffect(() => {
    if (!stateKey || isResizing) return;
    writePersistedTableState(stateKey, {
      pagination,
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      view: pivotView.view,
      savedView: appliedView,
      path,
      entry,
      left: undefined,
    });
  }, [
    appliedView,
    path,
    entry,
    pivotView.view,
    stateKey,
    pagination,
    sorting,
    columnVisibility,
    columnOrder,
    columnPinning,
    columnSizing,
    isResizing,
  ]);

  useRestoreScroll(stateKey, !isLoading && data.length > 0, persisted?.scrollY);

  const defaultVisibility = useMemo(() => {
    return {
      ...Object.fromEntries(
        table
          .getAllLeafColumns()
          .filter((column) => column.columnDef.meta?.defaultHidden)
          .map((column) => [column.id, false]),
      ),
      ...initialState?.columnVisibility,
    };
  }, [typedColumns]);

  // Columns that arrive later start hidden too, unless the user already chose
  useLayoutEffect(() => {
    const missing = Object.entries(defaultVisibility).filter(
      ([id]) => !(id in table.state.columnVisibility),
    );
    if (!missing.length) return;
    table.setColumnVisibility((current) => {
      return { ...Object.fromEntries(missing), ...current };
    });
  }, [defaultVisibility]);

  const resetColumns = () => {
    table.setColumnVisibility(defaultVisibility);
    table.setColumnOrder(initialState?.columnOrder ?? []);
  };

  // Server pages only hold the current rows, so keep each selected row as it's selected
  const { rowSelection } = table.state;
  const { rowsById } = table.getCoreRowModel();
  const selectedRowCache = useRef(new Map<string, TData>());
  Object.keys(rowSelection).forEach((id) => {
    if (rowsById[id]) selectedRowCache.current.set(id, rowsById[id].original);
  });
  selectedRowCache.current.forEach((_row, id) => {
    if (!rowSelection[id]) selectedRowCache.current.delete(id);
  });

  // Client data is complete, so selected rows that are no longer in it were removed; callers that control the selection manage it themselves
  useEffect(() => {
    if (isServer || selectedRowIds) return;
    const missing = Object.keys(rowSelection).filter((id) => !rowsById[id]);
    if (!missing.length) return;
    table.setRowSelection((current) => {
      const next = { ...current };
      missing.forEach((id) => delete next[id]);
      return next;
    });
  }, [data]);

  const getSelectedRows = () =>
    Object.keys(rowSelection)
      .filter((id) => rowSelection[id])
      .map((id) => rowsById[id]?.original ?? selectedRowCache.current.get(id))
      .filter((row): row is TData => row !== undefined);

  return Object.assign(table, {
    v2: pivotView,
    resetColumns,
    onSelectAllRows,
    allRowsSelected,
    restored: !!persisted,
    restoredView: persisted?.savedView,
    setAppliedView,
    defaultColumnVisibility: defaultVisibility,
    applyPageSize,
    getSelectedRows,
  });
};
